'use client'

import {
  extractLevelProfile,
  type LevelProfileAnalyticsType,
  type LevelProfileFor,
} from '@/service/analytics/level-profiles.types'
import { createStreamHook } from '@/service/ws/use-web-socket-with-proto'
import type { AnalyticsStream, ServerMessage } from '@/types/generated/public_api'

// `createStreamHook`'s listener is registered per-CHANNEL ('analyticsStream'), not per
// subscription — every chart subscribed to this analytics type (any symbol) receives every push
// on the channel. Unlike Amberdata's list-shaped responses (filtered client-side by currency),
// these responses are a single object per symbol, so the equivalent guard is a straight identity
// check: reject anything that isn't for the exact exchange+symbol this hook instance subscribed
// to, or two charts on different symbols would cross-contaminate each other's overlay.
// Exported standalone (not just inlined into createStreamHook's config) so the identity filter
// is unit-testable without a WS/React harness.
export function extractLevelProfileStreamResponse(
  message: ServerMessage,
  payload: AnalyticsStream
): LevelProfileFor<LevelProfileAnalyticsType> | undefined {
  const profile = extractLevelProfile(
    message.data,
    payload.analyticsType as LevelProfileAnalyticsType
  )
  if (!profile) return undefined
  // PerpPositionsSnapshot names its instrument `instrument`; the level profiles say `symbol`.
  const symbol = 'instrument' in profile ? profile.instrument : profile.symbol
  if (profile.exchange !== payload.exchange || symbol !== payload.symbol) return undefined
  return profile
}

export const useLevelProfileStream = createStreamHook<
  AnalyticsStream,
  LevelProfileFor<LevelProfileAnalyticsType>
>({
  channelKey: 'analyticsStream',
  extractResponse: extractLevelProfileStreamResponse,
  getPayloadDeps: (p) => [p.exchange, p.symbol, p.analyticsType],
  listenerChannelKey: 'analyticsStream',
})
